/**
 * Composable that owns the V2 individual dependent-exclusion form's
 * reactive state and side-effects.
 *
 * The SFC at
 *   individual-dependants-exclusion-form.vue
 * is a thin presentation layer on top of this composable. Keeping the
 * logic here means the form behaviour is unit-testable without a
 * full Vue mount + ant-design-vue render.
 *
 * See CONTEXT.md for the domain glossary and the cascading-filter
 * hierarchy (Company -> Insurance -> Policy -> Group -> Coverage ->
 * Dependent). The V2 endpoint
 *   GET /api/v2/employees/:employeeId/active-coverages-with-dependents
 * returns the full tree; the form filters it locally.
 */

import { computed, nextTick, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import { normalizePicker } from '@/utils/calendarDay';
import { useStore } from 'vuex';
import { employeeService } from '../../../../../../../network';
import {
  FETCH_V2_EMPLOYEE_ACTIVE_COVERAGES_WITH_DEPENDENTS,
  POST_V2_DEPENDENTS_EXCLUSION
} from '../../../../../../../store/ticket/dependents-exclusion/individualExclusion/types';
import { GET_LOGGED_USER } from '../../../../../../../store/auth/types';
import {
  buildExclusionItems,
  buildExclusionPayload,
  extractCompanies,
  extractInsurances,
  extractPolicies,
  filterCoveragesByPolicies,
  getActiveDependents,
  groupCoveragesByPolicyAndGroup,
  isTreeEmpty,
  normalizeFromBackend,
  treeHasNoDependents
} from './exclusion-form-helpers';
import rutHelper from '../../../../../../../utils/Ruts';
import { getInsuranceBrokerWarning } from '../../../../../../../utils/insuranceBroker';

export const EXCLUSION_DEFAULT_MESSAGE =
  'A través este medio solicitamos la siguiente baja de cargas para el titular de RUT:';

export const useExclusionForm = () => {
  const store = useStore();

  const formRef = ref(null);
  const formState = reactive({
    rut: '',
    endDate: null,
    company: null,
    insurance: null,
    policies: [],
    message: EXCLUSION_DEFAULT_MESSAGE
  });

  // Domain state
  const employeeId = ref(null);
  const tree = ref({ coverages: [] });
  const loadingTree = ref(false);
  const treeError = ref('');
  const submitting = ref(false);
  const showSuccess = ref(false);
  const showError = ref(false);
  const createExclusionError = ref('');
  const createExclusionMsg = ref('');
  const createdTicketId = ref('');

  /**
   * { [employeeCoverageId]: number[] } — which dependents the user
   * ticked for each employee coverage.
   */
  const selectedDependents = reactive({});

  // ─── Cascading-filter computeds (local, no extra API calls) ─────
  const dataCompany = computed(() => extractCompanies(tree.value));
  const dataInsurance = computed(() =>
    extractInsurances(tree.value, formState.company)
  );
  const dataPolicy = computed(() =>
    extractPolicies(tree.value, {
      companyId: formState.company,
      insuranceId: formState.insurance
    })
  );
  const filteredCoverages = computed(() =>
    filterCoveragesByPolicies(tree.value, formState.policies)
  );
  const filteredCoverageGroups = computed(() =>
    groupCoveragesByPolicyAndGroup(filteredCoverages.value)
  );
  // Same set the backend validates: every policy selected in the filter, since
  // all of them are sent in the payload.
  const insuranceBrokerWarning = computed(() =>
    getInsuranceBrokerWarning(
      filteredCoverages.value.map((coverage) => coverage.policy)
    )
  );
  const hasFilteredTree = computed(() => {
    if (!formState.policies || formState.policies.length === 0) return false;
    return filteredCoverageGroups.value.length > 0;
  });

  // ─── RUT handling ──────────────────────────────────────────────
  const lastValidatedRut = ref('');

  const resetCascading = () => {
    formState.company = null;
    formState.insurance = null;
    formState.policies = [];
    for (const key of Object.keys(selectedDependents)) {
      delete selectedDependents[key];
    }
  };

  const resetTree = () => {
    tree.value = { coverages: [] };
    treeError.value = '';
    lastValidatedRut.value = '';
    resetCascading();
  };

  const onRutChange = () => {
    if (!formState.rut) {
      employeeId.value = null;
      resetTree();
    }
  };

  const validateAndFetchTree = async (rule, value) => {
    if (!value) {
      return Promise.reject('Ingrese RUT');
    }
    if (!rutHelper.validateRut(value)) {
      return Promise.reject('RUT no válido');
    }

    const normalized = rutHelper.normalizeRut(value);

    // Short-circuit: same RUT already validated and tree loaded.
    // Prevents wiping selections when ant-design-vue re-runs this
    // validator during formRef.validate() on submit.
    if (
      lastValidatedRut.value === normalized &&
      tree.value.coverages.length > 0 &&
      !treeError.value
    ) {
      return Promise.resolve();
    }

    try {
      loadingTree.value = true;
      treeError.value = '';
      resetCascading();

      const employee = await employeeService.getEmployee({ rut: normalized });
      if (!employee?.id) {
        employeeId.value = null;
        lastValidatedRut.value = '';
        treeError.value = `No se encontró al titular con RUT ${normalized}.`;
        return Promise.reject(treeError.value);
      }
      employeeId.value = employee.id;

      const fetchedTree = await store.dispatch(
        FETCH_V2_EMPLOYEE_ACTIVE_COVERAGES_WITH_DEPENDENTS,
        { employeeId: employee.id }
      );
      const safeTree = normalizeFromBackend(fetchedTree);
      tree.value = safeTree;

      if (isTreeEmpty(safeTree) || treeHasNoDependents(safeTree)) {
        lastValidatedRut.value = '';
        treeError.value =
          'El titular no tiene coberturas activas con cargas para excluir.';
        return Promise.reject(treeError.value);
      }
      lastValidatedRut.value = normalized;
      return Promise.resolve();
    } catch (err) {
      lastValidatedRut.value = '';
      if (!treeError.value) {
        treeError.value =
          err?.response?.data?.message ||
          err?.cause?.response?.data?.message ||
          err?.message ||
          'No se pudo obtener la información del titular.';
      }
      return Promise.reject(treeError.value);
    } finally {
      loadingTree.value = false;
    }
  };

  // ─── Cascading change handlers ─────────────────────────────────
  const onCompanyChange = () => {
    formState.insurance = null;
    formState.policies = [];
  };
  const onInsuranceChange = () => {
    formState.policies = [];
  };
  const onPolicyChange = () => {
    // Drop dependent selections that no longer belong to the visible
    // (filtered) coverages, so we never submit orphans.
    const visibleIds = new Set(filteredCoverages.value.map((c) => c.id));
    for (const key of Object.keys(selectedDependents)) {
      if (!visibleIds.has(Number(key))) {
        delete selectedDependents[key];
      }
    }
  };

  // ─── Dependent checkboxes ──────────────────────────────────────
  const onDependentToggle = (coverageId, values) => {
    if (!values || values.length === 0) {
      delete selectedDependents[coverageId];
    } else {
      selectedDependents[coverageId] = values.map((v) => Number(v));
    }
  };

  // ─── Form rules ────────────────────────────────────────────────
  const rules = {
    rut: [
      { required: true, validator: validateAndFetchTree, trigger: 'change' }
    ],
    endDate: [
      {
        required: true,
        message: 'Seleccione una fecha de baja',
        trigger: 'change',
        type: 'object'
      }
    ],
    company: [
      {
        required: true,
        message: 'Seleccione una empresa',
        trigger: 'change',
        type: 'number'
      }
    ],
    insurance: [
      {
        required: true,
        message: 'Seleccione una aseguradora',
        trigger: 'change',
        type: 'number'
      }
    ],
    policies: [
      {
        type: 'array',
        required: true,
        min: 1,
        message: 'Seleccione al menos una póliza',
        trigger: 'change'
      }
    ]
  };

  // ─── Submit ────────────────────────────────────────────────────
  const onSubmitClick = async () => {
    if (formRef.value?.validate) {
      try {
        await formRef.value.validate();
      } catch (_err) {
        return; // ant-design-vue will show the field errors
      }
    }
    if (Object.keys(selectedDependents).length === 0) {
      message.error('Seleccione al menos una carga a dar de baja.');
      return;
    }
    if (insuranceBrokerWarning.value) return;

    try {
      submitting.value = true;
      const endDate = normalizePicker(formState.endDate);
      const items = buildExclusionItems(selectedDependents);
      const loggedUser = store.getters[GET_LOGGED_USER];
      const payload = buildExclusionPayload({
        endDate,
        items,
        companyId: formState.company,
        brokerId: loggedUser?.id,
        insuranceCompanyId: formState.insurance,
        rut: rutHelper.normalizeRut(formState.rut),
        message: formState.message,
        policies: formState.policies
      });

      const result = await store.dispatch(POST_V2_DEPENDENTS_EXCLUSION, {
        payload
      });

      const ticketId =
        result?.id || result?.ticket?.id || result?.ticketId || '';
      createdTicketId.value = String(ticketId);
      createExclusionMsg.value = result?.message || '';
      showSuccess.value = true;
      resetForm();
    } catch (err) {
      createExclusionError.value =
        err?.response?.data?.message ||
        err?.cause?.response?.data?.message ||
        err?.message ||
        'No se pudo crear el ticket.';
      showError.value = true;
    } finally {
      submitting.value = false;
    }
  };

  const resetForm = async () => {
    formState.rut = '';
    formState.endDate = null;
    formState.company = null;
    formState.insurance = null;
    formState.policies = [];
    formState.message = EXCLUSION_DEFAULT_MESSAGE;
    employeeId.value = null;
    lastValidatedRut.value = '';
    resetTree();
    // Wait for Vue to flush reactive state changes before clearing
    // the ant-design-vue form fields, so resetFields does not race
    // with pending watchers.
    await nextTick();
    formRef.value?.resetFields?.();
  };

  return {
    // refs
    formRef,
    formState,
    employeeId,
    tree,
    loadingTree,
    treeError,
    submitting,
    showSuccess,
    showError,
    createExclusionError,
    createExclusionMsg,
    createdTicketId,
    selectedDependents,
    // computeds
    dataCompany,
    dataInsurance,
    dataPolicy,
    filteredCoverages,
    filteredCoverageGroups,
    hasFilteredTree,
    insuranceBrokerWarning,
    // methods
    onRutChange,
    onCompanyChange,
    onInsuranceChange,
    onPolicyChange,
    onDependentToggle,
    onSubmitClick,
    validateAndFetchTree,
    resetForm,
    getActiveDependents,
    rules
  };
};
